export type Frequency = 'daily' | 'weekly';
export type Rating = 1 | 2 | 3 | 4 | 5;

export interface Habit {
  id: string;
  name: string;
  category: string;            // tag for filtering; '' = untagged
  frequency: Frequency;
  target?: number;             // required when frequency === 'weekly'
  createdAt: number;
  archivedAt?: number;         // soft-delete; hidden from today, kept in history
}

export interface HabitCompletion {
  id: string;
  habitId: string;
  date: string;                // 'YYYY-MM-DD' local time
  note?: string;
  createdAt: number;
}

export interface JournalEntry {
  date: string;                // 'YYYY-MM-DD' — also the Firebase key
  text?: string;
  generalRating?: Rating;
  happinessRating?: Rating;
  updatedAt: number;
}

export interface Countdown {
  targetDate: string;          // 'YYYY-MM-DD'
  label?: string;
}

export interface MigrationGuard {
  completedAt: number;
  itemsConverted: {
    habits: number;
    completions: number;
    weeklyGoals: number;
  };
}

// ─── Mandala visualization ─────────────────────────────────────────

export type MotifId =
  | 'slim-petal' | 'round-petal' | 'leaf' | 'triangle' | 'spike'
  | 'diamond' | 'dot' | 'star-5' | 'burst' | 'hexagon'
  | 'crescent' | 'teardrop' | 'trefoil' | 'rosette' | 'plus' | 'curl';

export const ALL_MOTIF_IDS: MotifId[] = [
  'slim-petal', 'round-petal', 'leaf', 'triangle', 'spike',
  'diamond', 'dot', 'star-5', 'burst', 'hexagon',
  'crescent', 'teardrop', 'trefoil', 'rosette', 'plus', 'curl',
];

export type TierName = 'seed' | 'sprout' | 'bud' | 'bloom' | 'flourish' | 'mastery';

export interface TierConfig {
  tier: TierName;
  days: number;          // streak threshold (>=)
  motifCount: number;    // primary motifs around the ring
  accentDots: boolean;   // small dots interleaved between motifs
  haloBoost: boolean;    // ~2x halo opacity
  innerEcho: boolean;    // smaller motif copy inset toward the center
  rotate: boolean;       // slow ~60s/revolution rotation
}

export const TIER_THRESHOLDS: TierConfig[] = [
  { tier: 'seed',     days: 0,  motifCount: 0,  accentDots: false, haloBoost: false, innerEcho: false, rotate: false },
  { tier: 'sprout',   days: 1,  motifCount: 4,  accentDots: false, haloBoost: false, innerEcho: false, rotate: false },
  { tier: 'bud',      days: 3,  motifCount: 8,  accentDots: false, haloBoost: false, innerEcho: false, rotate: false },
  { tier: 'bloom',    days: 7,  motifCount: 12, accentDots: true,  haloBoost: false, innerEcho: false, rotate: false },
  { tier: 'flourish', days: 14, motifCount: 12, accentDots: true,  haloBoost: true,  innerEcho: true,  rotate: false },
  { tier: 'mastery',  days: 30, motifCount: 16, accentDots: true,  haloBoost: true,  innerEcho: true,  rotate: true  },
];

export interface CategoryStyle {
  motif: MotifId;
  color: string;  // hex like '#d4baee'
}

export const DEFAULT_FESTIVE_MIX: CategoryStyle[] = [
  { motif: 'burst',    color: '#d4baee' }, // lilac
  { motif: 'crescent', color: '#fcd590' }, // gold
  { motif: 'dot',      color: '#facadc' }, // pink
  { motif: 'rosette',  color: '#bee9ca' }, // mint
];

export interface MandalaPrefs {
  ringOrder: string[];                            // category names, innermost first
  styles: { [category: string]: CategoryStyle };  // motif + color per category
  visibleCategories?: string[];                   // only used when active categories > 6
  updatedAt: number;
}

// Derived (not stored)

/** One position on a ring — bound to a specific habit. */
export interface HabitSlot {
  habitId: string;
  habitName: string;
  completedToday: boolean;
}

export interface RingState {
  category: string;
  habitIds: string[];
  /** One slot per non-archived habit in the category, sorted by habit createdAt. */
  slots: HabitSlot[];
  streak: number;
  tier: TierName;
  motif: MotifId;
  color: string;
  /** Streak >= RING_ROTATE_STREAK_THRESHOLD — the whole ring rotates slowly. */
  ringRotates: boolean;
  /** Streak >= PETAL_SPIN_STREAK_THRESHOLD — each petal also spins on its own axis. */
  petalsSpin: boolean;
  /** 5 booleans for the last 5 days, oldest at index 0, today at index 4. */
  last5: boolean[];
  decayedThisLoad: boolean;
}

/** Streak (in days) at which the whole ring starts rotating. */
export const RING_ROTATE_STREAK_THRESHOLD = 2;
/** Streak (in days) at which each petal also spins on its own axis. */
export const PETAL_SPIN_STREAK_THRESHOLD = 5;

export const MAX_VISIBLE_RINGS = 6;
